import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Activity, BookOpen, NotebookPen, Sun, Mail, CalendarClock, ChevronDown, Settings, Sparkles, Boxes, FolderKanban, Gauge, LayoutDashboard, Cable, CalendarRange, Building2, ClipboardCheck, GitBranch, Grid2x2, History, Home, Inbox, KeyRound, ListChecks, LogOut, Menu, Plus, Radio, Scale, ShieldCheck, Stamp, Users, Wallet, X, FolderOpen, ExternalLink } from "lucide-react";
import { useEffect, useRef, useState, type ComponentType } from "react";
import { Avatar } from "./ui";
import { post } from "../lib/api";
import { clearOutbox, discard, retry, useOutbox } from "../lib/offline";
import { useCan, useSession } from "../lib/session";
import { useInbox } from "../pages/inbox";
import { CreateSheet } from "../pages/create";
import { NewSheet } from "./QuickAdd";
import { DoneNextBar } from "./DoneNext";
import { UpdateBar } from "./UpdateBar";
import { SearchBox } from "../pages/insights";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useWealthAccess } from "./wealth/shared";
import { FusionAiButton } from "./assistant/FusionAi";
import { UndoBar } from "./UndoBar";
import { useFusionDocs } from "./FusionDocs";

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number; className?: string }>;
  perm?: string;
  /** Private page: shown only when its own access check says so. */
  gate?: "wealth" | "fusiondocs";
  /** Another platform app: opens in a new tab. */
  href?: string;
}

/** One flow for everyone: New → Inbox → Today → Tasks. Specialist tools sit under "More". */
const GROUPS: Array<{ label: string | null; items: NavItem[] }> = [
  {
    label: null,
    items: [
      { to: "/", label: "Today", icon: Sun },
      { to: "/inbox", label: "Inbox", icon: Inbox },
      { to: "/assistant", label: "Fusion AI", icon: Sparkles },
      { to: "/work", label: "Tasks", icon: ListChecks, perm: "work.task.view" },
      { to: "/notes", label: "Notes", icon: NotebookPen },
      { to: "/knowledge", label: "Knowledge", icon: BookOpen },
      { to: "/wealth", label: "Wealth", icon: Wallet, gate: "wealth" },
      { to: "/documents", label: "Documents", icon: FolderOpen, gate: "fusiondocs" },
      { to: "/mail", label: "Mail", icon: Mail, perm: "mail.assistant.use" },
    ],
  },
  {
    label: "Teams",
    items: [{ to: "/teams", label: "My teams", icon: Users, perm: "work.task.view" }],
  },
  {
    label: "More",
    items: [
      { to: "/projects", label: "Projects", icon: FolderKanban },
      { to: "/planner", label: "Planner", icon: CalendarRange, perm: "work.planner.view" },
      { to: "/crew-board", label: "Crew Board", icon: Activity, perm: "work.crewboard.view" },
      { to: "/approvals", label: "Approvals history", icon: Stamp },
      { to: "/workflows", label: "Workflows", icon: GitBranch },
      { to: "/inspections", label: "Inspections", icon: ClipboardCheck, perm: "inspections.run.perform" },
      { to: "/assets", label: "Assets", icon: Boxes, perm: "assets.asset.view" },
      { to: "/pulse", label: "Group Pulse", icon: Gauge, perm: "approvals.request.view" },
      { to: "/apps", label: "All apps", icon: Grid2x2, perm: "platform.launcher.view" },
    ],
  },
];
const ADMIN: NavItem[] = [
  { to: "/admin/organisation", label: "Organisation", icon: Building2, perm: "directory.org.view" },
  { to: "/admin/people", label: "People", icon: Users, perm: "directory.person.view" },
  { to: "/admin/roles", label: "Roles", icon: KeyRound, perm: "authz.role.view" },
  { to: "/admin/grants", label: "Access", icon: ShieldCheck, perm: "authz.grant.view" },
  { to: "/admin/approvals", label: "Approval policies", icon: Scale, perm: "approvals.request.view" },
  { to: "/admin/inspection-schedules", label: "Inspection schedules", icon: CalendarClock, perm: "inspections.template.admin" },
  { to: "/admin/audit", label: "Audit log", icon: History, perm: "audit.log.view" },
  { to: "/admin/integrations", label: "Integrations", icon: Cable, perm: "events.outbox.view" },
  { to: "/admin/events", label: "Events", icon: Radio, perm: "events.outbox.view" },
];

function useInboxCount() {
  const inbox = useInbox();
  return Object.values(inbox.data?.counts ?? {}).reduce((a, b) => a + b, 0);
}

function NavList({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  const can = useCan();
  const inboxCount = useInboxCount();
  const mailCount = useQuery({
    queryKey: ["mail", "status"],
    queryFn: () => api<{ counts: { attention: number } }>("/api/mail/status"),
    enabled: can("mail.assistant.use"),
    staleTime: 60_000,
    refetchInterval: 120_000,
  }).data?.counts.attention ?? 0;
  const wealth = useWealthAccess().data?.enabled === true;
  const fusionDocs = useFusionDocs();
  const path = useRouterState({ select: (s) => s.location.pathname });
  return (
    <ul className="space-y-px">
      {items
        .filter((i) => (!i.perm || can(i.perm)) && (i.gate !== "wealth" || wealth) && (i.gate !== "fusiondocs" || fusionDocs))
        .map((i) => {
          if (i.href) return (
            <li key={i.to}>
              <a href={i.href} target="_blank" rel="noreferrer" onClick={onNavigate}
                className="group relative flex items-center gap-3 rounded-[10px] px-3 py-[7px] text-[0.875rem] font-medium text-white/60 transition-colors hover:bg-white/[0.06] hover:text-white">
                <i.icon size={17} className="text-white/45 group-hover:text-white/80" />
                <span className="truncate">{i.label}</span>
                <ExternalLink size={12} className="ml-auto text-white/35" />
              </a>
            </li>
          );
          const active = i.to === "/" ? path === "/" : path === i.to || path.startsWith(`${i.to}/`);
          return (
            <li key={i.to}>
              <Link
                to={i.to}
                onClick={onNavigate}
                className={clsx(
                  "group relative flex items-center gap-3 rounded-[10px] px-3 py-[7px] text-[0.875rem] font-medium transition-colors",
                  active ? "bg-brand-600 text-white shadow-[0_1px_2px_rgb(0_0_0/0.3),inset_0_1px_0_rgb(255_255_255/0.12)]" : "text-white/60 hover:bg-white/[0.06] hover:text-white",
                )}
              >
                <i.icon size={17} className={active ? "text-white" : "text-white/45 group-hover:text-white/80"} />
                <span className="truncate">{i.label}</span>
                {i.to === "/mail" && mailCount > 0 && (
                  <span className={clsx("ml-auto rounded-full px-1.5 py-px text-[11px] font-bold num", active ? "bg-white text-brand-700" : "bg-white/10 text-white/90")}>{mailCount > 99 ? "99+" : mailCount}</span>
                )}
                {i.to === "/inbox" && inboxCount > 0 && (
                  <span className={clsx("ml-auto rounded-full px-1.5 py-px text-[11px] font-bold num", active ? "bg-white text-brand-700" : "bg-white/10 text-white/90")}>{inboxCount > 99 ? "99+" : inboxCount}</span>
                )}
              </Link>
            </li>
          );
        })}
    </ul>
  );
}

function Sidebar({ onNavigate, onNew }: { onNavigate?: () => void; onNew?: () => void }) {
  const can = useCan();
  const showAdmin = ADMIN.some((i) => !i.perm || can(i.perm));
  return (
    <nav aria-label="Main" className="flex h-full flex-col">
      <Link to="/" onClick={onNavigate} className="flex items-center gap-3 px-5 pb-5 pt-5" aria-label="Infinite Fusion ONE — home">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white p-[3px] shadow-[0_2px_8px_rgb(0_0_0/0.35)]">
          <img src="/brand/mark.png" alt="" className="h-8 w-auto" />
        </span>
        <span className="leading-tight">
          <span className="block text-[0.9375rem] font-extrabold tracking-tight text-white">ONE</span>
          <span className="block text-xs font-medium text-white/45">Infinite Fusion</span>
        </span>
      </Link>
      {onNew && (
        <div className="px-3 pb-4">
          <button className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700" onClick={() => { onNavigate?.(); onNew(); }}>
            <Plus size={16} strokeWidth={2.5} /> New
          </button>
        </div>
      )}
      <div className="flex-1 space-y-5 overflow-y-auto px-3 pb-6">
        {GROUPS.map((g) => {
          const visible = g.items.filter((i) => !i.perm || can(i.perm));
          if (!visible.length) return null;
          return (
            <div key={g.label ?? "top"}>
              {g.label && <div className="mb-1.5 px-3 text-[10.5px] font-bold uppercase tracking-[0.12em] text-white/35">{g.label}</div>}
              <NavList items={visible} onNavigate={onNavigate} />
            </div>
          );
        })}
        {showAdmin && (
          <div>
            <div className="mb-1.5 px-3 text-[10.5px] font-bold uppercase tracking-[0.12em] text-white/35">Admin</div>
            <NavList items={ADMIN} onNavigate={onNavigate} />
          </div>
        )}
      </div>
    </nav>
  );
}

/** Avatar button with My settings and Sign out. */
function UserMenu({ onLogout }: { onLogout: () => void }) {
  const s = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button className="flex items-center gap-2.5 rounded-xl py-1 pl-1 pr-2 transition-colors hover:bg-[#f1f2f8]" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
        <Avatar name={s.person?.name} size={34} />
        <span className="hidden text-left leading-tight lg:block">
          <span className="block text-sm font-semibold">{s.person?.name}</span>
          <span className="muted block text-xs">{s.person?.email}</span>
        </span>
        <ChevronDown size={15} className="muted hidden lg:block" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-40 mt-2 w-60 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-1.5 shadow-[var(--shadow-pop)]">
          <div className="border-b border-[var(--line)] px-3 pb-2.5 pt-1.5 lg:hidden">
            <div className="text-sm font-semibold">{s.person?.name}</div>
            <div className="muted text-xs">{s.person?.email}</div>
          </div>
          <Link to="/settings" role="menuitem" onClick={() => setOpen(false)} className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium hover:bg-[#f4f5fb]">
            <Settings size={16} className="muted" /> My settings
          </Link>
          <button role="menuitem" onClick={onLogout} className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-red-600 hover:bg-red-50">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** Connection + offline-queue status for workers in the field. */
function OfflineBanner() {
  const o = useOutbox();
  const pending = [...o.items.filter((i) => i.status === "pending"), ...o.requests.filter((i) => i.status === "pending")];
  const failed = o.items.filter((i) => i.status === "failed");
  const failedReqs = o.requests.filter((i) => i.status === "failed");
  if (o.online && !pending.length && !failed.length && !failedReqs.length) return null;
  return (
    <div className="border-b border-amber-200 bg-amber-50 px-6 py-2.5 text-sm text-amber-900">
      {!o.online ? (
        <span><strong>Offline.</strong> You can keep working{pending.length ? ` — ${pending.length} update${pending.length > 1 ? "s" : ""} saved on this phone` : ""}. They send automatically when the signal is back.</span>
      ) : pending.length ? (
        <span>{o.syncing ? "Sending" : "Waiting to send"} {pending.length} update{pending.length > 1 ? "s" : ""} saved offline…</span>
      ) : null}
      {failed.map((f) => (
        <div key={f.clientRef} className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-red-700">Couldn't save your update on “{f.itemTitle}”: {f.error}</span>
          <button className="btn btn-sm" onClick={() => void retry(f.clientRef)}>Try again</button>
          <Link className="btn btn-sm" to="/work/$id" params={{ id: f.itemId }}>Open job</Link>
          <button className="btn btn-sm" onClick={() => { if (confirm("Discard this update? The note and photos saved on this phone will be deleted.")) void discard(f.clientRef); }}>Discard</button>
        </div>
      ))}
      {failedReqs.map((f) => (
        <div key={f.clientRef} className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-red-700">Couldn't send “{f.label}”: {f.error}</span>
          <button className="btn btn-sm" onClick={() => void retry(f.clientRef)}>Try again</button>
          {f.itemId && <Link className="btn btn-sm" to="/work/$id" params={{ id: f.itemId }}>Open task</Link>}
          <button className="btn btn-sm" onClick={() => { if (confirm("Discard this change? It was only saved on this phone.")) void discard(f.clientRef); }}>Discard</button>
        </div>
      ))}
    </div>
  );
}

export function Shell() {
  const s = useSession();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const can = useCan();
  const inboxCount = useInboxCount();
  const path = useRouterState({ select: (st) => st.location.pathname });
  const logout = async () => {
    await post("/api/session/logout", {});
    qc.clear();
    // Shared phones: drop this person's offline copies and anything still queued.
    await clearOutbox();
    if ("caches" in window) await caches.delete("one-api-v1").catch(() => false);
    window.location.href = "/login";
  };
  return (
    <div className="flex h-full">
      <aside className="rail hidden w-[248px] shrink-0 md:block">
        <Sidebar onNew={() => setNewOpen(true)} />
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 bg-[#0b0c2a]/50 backdrop-blur-[2px] md:hidden" onClick={() => setOpen(false)}>
          <aside className="rail h-full w-[272px] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <Sidebar onNavigate={() => setOpen(false)} onNew={() => setNewOpen(true)} />
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center justify-between gap-4 border-b border-[var(--line)] bg-[var(--panel)]/90 px-4 backdrop-blur md:px-6 xl:px-8">
          <div className="flex items-center gap-2 md:hidden">
            <button className="btn btn-ghost btn-icon" onClick={() => setOpen((o) => !o)} aria-label="Open menu">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <img src="/brand/mark.png" alt="Infinite Fusion" className="h-8 w-auto" />
          </div>
          <div className="hidden max-w-xl flex-1 md:block"><SearchBox /></div>
          <div className="flex items-center gap-2 md:gap-3">
            {can("work.task.create") && (
              <button className="btn btn-primary hidden md:inline-flex" onClick={() => setNewOpen(true)}><Plus size={17} strokeWidth={2.5} /> New</button>
            )}
            <span className="hidden h-6 w-px bg-[var(--line)] md:block" />
            <UserMenu onLogout={logout} />
          </div>
        </header>
        <OfflineBanner />
        <main className="scroll-thin min-w-0 flex-1 overflow-auto px-4 pb-28 pt-5 md:px-6 md:pb-10 md:pt-7 xl:px-8">
          <Outlet />
          <DoneNextBar />
          <UpdateBar />
        </main>
        {/* Mobile bottom tabs: Today · Inbox · ＋ · Tasks · More */}
        <nav aria-label="Mobile" className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-[var(--line)] bg-[var(--panel)]/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
          {[
            { to: "/", label: "Today", icon: Sun },
            { to: "/inbox", label: "Inbox", icon: Inbox, badge: inboxCount },
          ].map((t) => {
            const on = t.to === "/" ? path === "/" : path === t.to || path.startsWith(`${t.to}/`);
            return (
              <Link key={t.to} to={t.to} className={clsx("flex flex-col items-center gap-1 pb-2 pt-2.5 text-[11px] font-semibold", on ? "text-brand-600" : "text-[var(--muted)]")}>
                <span className="relative"><t.icon size={21} strokeWidth={on ? 2.4 : 2} />{t.badge ? <span className="absolute -right-2.5 -top-1.5 rounded-full bg-brand-600 px-1 text-[10px] font-bold text-white ring-2 ring-white num">{t.badge > 99 ? "99+" : t.badge}</span> : null}</span>
                {t.label}
              </Link>
            );
          })}
          <button className="flex flex-col items-center justify-center" onClick={() => setNewOpen(true)} aria-label="New">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand-600 text-white shadow-[0_6px_16px_-4px_rgb(43_53_255/0.6)]"><Plus size={22} strokeWidth={2.5} /></span>
          </button>
          <Link to="/work" className={clsx("flex flex-col items-center gap-1 pb-2 pt-2.5 text-[11px] font-semibold", path === "/work" || path.startsWith("/work/") ? "text-brand-600" : "text-[var(--muted)]")}><ListChecks size={21} />Tasks</Link>
          <button className="flex flex-col items-center gap-1 pb-2 pt-2.5 text-[11px] font-semibold text-[var(--muted)]" onClick={() => setOpen(true)}><Menu size={21} />More</button>
        </nav>
      </div>
      {newOpen && <NewSheet onClose={() => setNewOpen(false)} onAskTeam={() => { setNewOpen(false); setCreating(true); }} />}
      {creating && <CreateSheet onClose={() => setCreating(false)} />}
      <FusionAiButton />
      <UndoBar />
    </div>
  );
}
